import * as React from "react";
import { createPortal } from "react-dom";

import { composeRippleProps, useTouchRipple } from "@/components/touch-ripple";
import { useDialogPortalContainer } from "@/components/ui/dialog-portal-container";
import { MUI_MENU_ITEM, MUI_MENU_LIST, MUI_MENU_SURFACE } from "@/components/ui/mui-field-shape";
import { pinEnterStart, pinExitStart, setGrowClock } from "@/components/ui/mui-grow";
import {
  acquireScrollLock,
  freshTypeahead,
  hideSiblings,
  makeRoomForScrollbar,
  placePaper,
  releasedOn,
  RESIZE_DEBOUNCE_MS,
  typeaheadStep,
  UNSELECTED_MOUSE_UP_DELAY_MS,
  type OpenedBy,
} from "@/components/ui/mui-select-layer";
import { cn } from "@/lib/utils";

// MUI's non-native `Select` whose `Menu` opens at `disableScrollLock` — what
// `MRT_TablePagination` hands its rows-per-page control (`MenuProps: {
// disableScrollLock: true }`, MRT `index.esm.js:3978`). Every other Atlas select
// is Radix's, whose content locks the page's scroll unconditionally; this one
// cannot be, so it is built on MUI's own layers rather than on Radix's
// (#698's decision round, D8 — the owner ruled the lock back to parity).
//
// Read off MUI 9.2.0, which the donor runs:
//
//   - **the field** — `SelectInput`'s display: a `div role="combobox"`, a tab
//     stop, `aria-expanded`, `aria-haspopup="listbox"`, `aria-controls` while
//     open (`SelectInput.js:714-736`). It opens on a primary *press*, focusing
//     itself first, and on Space, ArrowUp, ArrowDown or Enter (`:378-418,
//     536-549`); closed, a printable key picks the option it spells without
//     opening, a 750ms buffer (`:489-535`);
//   - **the layer** — `Menu` → `Popover` → `Modal`: a fixed root over the
//     viewport with an invisible backdrop under the paper, so nothing on the
//     page can be pressed while it is up and a *click* on the backdrop closes it
//     (`useModal.js:125-133`); Escape closes it; the page's other top-level
//     nodes are `aria-hidden` meanwhile (`ModalManager.js`); focus goes back to
//     the field on close (`FocusTrap.js`). **No scroll lock**: the page keeps
//     its overflow and its wheel, and the paper follows the field as the page
//     scrolls — `Popover` re-places it on every window scroll at
//     `disableScrollLock` (`Popover.js:250-255`), on every render while open
//     (`:262-266`) and on a debounced resize (`:272-285`), so a field scrolled
//     out of view leaves the paper pinned at the margin;
//   - **the placement** — the menu's top-centre on the field's bottom-centre
//     (`SelectInput.js:772-779`), at least the field's width (`:689-691`),
//     pushed back inside a 16px margin with its transform origin moved by the
//     same amount (`Popover.js:190-232`), in a paper capped at `calc(100% -
//     96px)` (`Menu.js:52-58`), widened by the page's scrollbar size when its
//     list overflows (`adjustStyleForScrollbar`, `Menu.js:96-101`,
//     `MenuList.js:171-182`), growing on MUI's `Grow` at `timeout='auto'`;
//   - **the list** — `MenuList` at `variant='selectedMenu'` and
//     `disableListWrap`: focus opens on the selected option, visibly only when
//     the keyboard opened it (`MenuList.js:128-146`, `focusWithVisible.js`);
//     the arrows stop at the ends, Home and End jump, a printable key walks
//     the options on a 500ms buffer (`MenuList.js:203-238`); Tab closes it
//     (`Menu.js:107-114`);
//   - **an option** — a `MenuItem` (ripple, `action.hover`, `action.focus`, the
//     selected tint and its hover and focus steps — hover winning over focus on
//     a selected row, focus over hover on any other, `MenuItem.js:76-99`) that is
//     chosen by a click, Enter, or Space on keydown (`SelectInput.js:589-600`)
//     — and by the *release* of the press that opened the menu, once that
//     press has been held long enough: 200ms for another option, 400ms for the
//     selected one (`:355-376,479-490`).

const CLOSED_TYPEAHEAD_RESET_MS = 750;

export interface MuiMenuSelectOption {
  value: string;
  /** The option's text — what typeahead matches, and what renders by default. */
  label: string;
  /**
   * A rendered label in place of the text, in the menu row and in the field
   * when selected — MUI's `MenuItem` children and `Select`'s rendered value,
   * which may be any node (the Version dropdown's chips, #694). The text stays
   * what typeahead reads.
   */
  content?: React.ReactNode;
  /** MUI's `MenuItem disabled`: drawn at 0.38, never focused, typed to or
   * chosen (`MenuItem.js`; `closedTypeahead.js:51`). */
  disabled?: boolean;
}

interface MuiMenuSelectProps {
  value: string;
  options: readonly MuiMenuSelectOption[];
  onValueChange: (value: string) => void;
  "aria-label"?: string;
  /** A name read from elsewhere — a form field's hidden name and its own
   * display, as the donor's `SelectField` names its combobox. */
  "aria-labelledby"?: string;
  /** The field's id, for an `aria-labelledby` that reads its display. */
  id?: string;
  /** `SelectInput`'s `aria-required` (`SelectInput.js:727`). */
  required?: boolean;
  /**
   * `SelectInput` at `disabled`: `aria-disabled`, no tab stop, and neither a
   * press nor a key opens it (`SelectInput.js:693-698,720,730`).
   */
  disabled?: boolean;
  /** The field's own classes — the caller's input shape. */
  className?: string;
  /** Classes on the box around the field and its caret, which the menu hangs from. */
  anchorClassName?: string;
  /** The caret beside the field, told whether the menu is open. */
  caret?: (open: boolean) => React.ReactNode;
  /**
   * MUI's own default for a `Select`'s `Menu`: while it is up the page cannot
   * scroll — `ModalManager` sets `overflow: hidden` on `<body>` and pads its
   * right edge by the scrollbar it removed, so nothing shifts. Off by default
   * here because this file's first caller, the pager's rows-per-page control,
   * opens at `disableScrollLock` (#698 D8); a caller at MUI's default passes it.
   */
  scrollLock?: boolean;
  /** Told when the menu opens and closes — a frame holding its focus paint
   * while its menu is up, as MUI's `FormControl` stays `focused`. */
  onOpenChange?: (open: boolean) => void;
  /**
   * `MenuProps.disablePortal`: the menu renders inside the field's own box
   * rather than in a portal — still a fixed layer over the viewport, but a
   * DOM descendant of the field's box (the Editor's status pill, #809), as
   * `MuiMultiSelect`'s does.
   */
  disablePortal?: boolean;
  /**
   * `Select`'s `renderValue`: what the field draws for the chosen option, in
   * place of its menu row (the status pill's bare dot, #809).
   */
  renderValue?: (option: MuiMenuSelectOption) => React.ReactNode;
}

export function MuiMenuSelect({
  value,
  options,
  onValueChange,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  id,
  required = false,
  disabled = false,
  className,
  anchorClassName,
  caret,
  scrollLock = false,
  onOpenChange,
  disablePortal = false,
  renderValue,
}: MuiMenuSelectProps) {
  // Inside a dialog, the menu sits in the dialog's content: a Radix dialog's
  // focus trap and outside-press handling own everything else on the page.
  const portalContainer = useDialogPortalContainer();
  const listboxId = React.useId();
  const [open, setOpenState] = React.useState(false);
  // Mounted: open, or still growing out.
  const [present, setPresent] = React.useState(false);
  const [openedBy, setOpenedBy] = React.useState<OpenedBy>(null);
  // The option holding the list's one tab stop — the selected one until focus
  // moves (`useRovingTabIndex`).
  const [activeValue, setActiveValue] = React.useState<string | null>(null);

  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const fieldRef = React.useRef<HTMLDivElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const placedPaper = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const openRef = React.useRef(false);
  const mouseUp = React.useRef({
    allowSelected: false,
    allowUnselected: false,
    pressedOnOption: false,
    timers: [] as ReturnType<typeof setTimeout>[],
  });
  const openingRelease = React.useRef<(() => void) | null>(null);
  const closedTypeahead = React.useRef({
    buffer: "",
    previousSearchIndex: null as number | null,
    matchedIndex: null as number | null,
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
  });
  const openTypeahead = React.useRef(freshTypeahead());

  const hasSelected = options.some((option) => option.value === value);
  const firstEnabled = options.find((option) => !option.disabled);

  const resetMouseUpSelection = () => {
    const state = mouseUp.current;
    for (const timer of state.timers) clearTimeout(timer);
    state.timers = [];
    state.pressedOnOption = false;
    state.allowSelected = false;
    state.allowUnselected = false;
  };
  const clearOpeningRelease = () => {
    openingRelease.current?.();
    openingRelease.current = null;
  };
  const resetClosedTypeahead = () => {
    const state = closedTypeahead.current;
    clearTimeout(state.timer);
    state.buffer = "";
    state.previousSearchIndex = null;
    state.matchedIndex = null;
  };

  const setOpen = (next: boolean, by: OpenedBy) => {
    if (next === openRef.current) return;
    openRef.current = next;
    const paper = paperRef.current;
    if (next) {
      // A paper still growing out re-enters from the size it had reached.
      pinEnterStart(paper);
      if (paper) {
        placePaper(paper, anchorRef.current);
        makeRoomForScrollbar(paper, listRef.current);
        setGrowClock(paper);
      }
      resetClosedTypeahead();
      openTypeahead.current = freshTypeahead();
      setActiveValue(null);
      setOpenedBy(by);
      setPresent(true);
    } else {
      // `Grow` measures again at the exit, and shrinks from where it had got to.
      pinExitStart(paper);
      if (paper) setGrowClock(paper);
      resetMouseUpSelection();
      clearOpeningRelease();
      setOpenedBy(null);
      // `FocusTrap` hands focus back as the modal closes, not after the exit.
      fieldRef.current?.focus();
    }
    setOpenState(next);
    onOpenChange?.(next);
  };
  const close = () => setOpen(false, null);

  // The option's side of `scheduleMouseUpSelection`: a press that began on an
  // option is that option's click; a release that did not, chooses only once
  // the opening press has been held long enough.
  const pressedOnOption = () => {
    mouseUp.current.pressedOnOption = true;
  };
  const releaseChooses = (isSelected: boolean) => {
    const state = mouseUp.current;
    if (state.pressedOnOption) {
      state.pressedOnOption = false;
      return false;
    }
    return isSelected ? state.allowSelected : state.allowUnselected;
  };

  const choose = (next: string) => {
    mouseUp.current.pressedOnOption = false;
    if (next !== value) onValueChange(next);
    close();
  };

  // Placed as the paper mounts — which is where `Popover` places it, on
  // entering — and its Grow timed there, once per paper.
  const attachPaper = (node: HTMLDivElement | null) => {
    paperRef.current = node;
    if (!node || placedPaper.current === node) return;
    placedPaper.current = node;
    placePaper(node, anchorRef.current);
    makeRoomForScrollbar(node, listRef.current);
    setGrowClock(node);
  };

  // Focus opens on the selected option (`MenuList` at `autoFocusItem`,
  // `variant='selectedMenu'`), drawn as focused only when the keyboard opened
  // the menu.
  React.useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    // The selected row, else the first enabled one, else the list itself —
    // `MenuList` skips a disabled row for its opening focus.
    const items = optionsIn(list);
    const target = items.find((item) => item.getAttribute("aria-selected") === "true") ?? items[0];
    (target ?? list).focus(openedBy ? { focusVisible: openedBy === "keyboard" } : undefined);
  }, [open, openedBy]);

  // `ModalManager`'s scroll lock, when the caller asks for MUI's default.
  React.useEffect(() => {
    if (!open || !scrollLock) return;
    return acquireScrollLock();
  }, [open, scrollLock]);

  // `Modal` hides the rest of the page from assistive tech while it is up.
  React.useEffect(() => {
    if (!open) return;
    const root = rootRef.current;
    if (!root) return;
    const hidden = hideSiblings(root);
    return () => {
      for (const node of hidden) node.removeAttribute("aria-hidden");
    };
  }, [open]);

  // `Popover` re-places its paper on every render while open
  // (`Popover.js:262-266`)…
  React.useEffect(() => {
    if (open && paperRef.current) placePaper(paperRef.current, anchorRef.current);
  });

  // …on every window scroll while it is mounted, undebounced — the listener
  // `disableScrollLock` adds (`Popover.js:250-255`)…
  React.useEffect(() => {
    if (!present) return;
    const onScroll = () => {
      if (paperRef.current) placePaper(paperRef.current, anchorRef.current);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [present]);

  // …and on a resize, debounced (`Popover.js:272-285`).
  React.useEffect(() => {
    if (!open) return;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(settle);
      settle = setTimeout(() => {
        if (paperRef.current) placePaper(paperRef.current, anchorRef.current);
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(settle);
    };
  }, [open]);

  React.useEffect(
    () => () => {
      for (const timer of mouseUp.current.timers) clearTimeout(timer);
      openingRelease.current?.();
      clearTimeout(closedTypeahead.current.timer);
    },
    [],
  );

  const onFieldMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    fieldRef.current?.focus();
    // `scheduleMouseUpSelection`: the release of this press chooses an option
    // only once it has been held — 200ms for another option, 200ms more for
    // the selected one.
    resetMouseUpSelection();
    const state = mouseUp.current;
    if (!hasSelected) {
      state.timers.push(
        setTimeout(() => {
          state.allowSelected = true;
          state.allowUnselected = true;
        }, UNSELECTED_MOUSE_UP_DELAY_MS * 2),
      );
    } else {
      state.timers.push(
        setTimeout(() => {
          state.allowUnselected = true;
          state.timers.push(
            setTimeout(() => {
              state.allowSelected = true;
            }, UNSELECTED_MOUSE_UP_DELAY_MS),
          );
        }, UNSELECTED_MOUSE_UP_DELAY_MS),
      );
    }
    // A release off both the field and the menu closes it.
    clearOpeningRelease();
    const onRelease = (release: MouseEvent) => {
      openingRelease.current = null;
      if (releasedOn(release, fieldRef.current) || releasedOn(release, paperRef.current)) return;
      close();
    };
    document.addEventListener("mouseup", onRelease, { capture: true, once: true });
    openingRelease.current = () => document.removeEventListener("mouseup", onRelease, true);
    setOpen(true, "pointer");
  };

  // Closed, a printable key picks the option it spells, without opening
  // (`handleClosedTypeahead`).
  const typeAheadClosed = (event: React.KeyboardEvent) => {
    const state = closedTypeahead.current;
    const hasBuffer = state.buffer !== "";
    if (
      open ||
      event.nativeEvent.isComposing ||
      event.key.length !== 1 ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      (event.key === " " && !hasBuffer)
    )
      return false;
    if (event.key === " ") event.preventDefault();
    // Only the enabled options are searched (`closedTypeahead.js:51`); with
    // none, the key is swallowed and the buffer let go.
    const searchable = options.filter((option) => !option.disabled);
    if (searchable.length === 0) {
      if (event.key !== " ") resetClosedTypeahead();
      return true;
    }
    const labels = searchable.map((option) => option.label.trim().toLowerCase());
    if (state.buffer === "")
      state.previousSearchIndex = searchable.findIndex((o) => o.value === value);
    const key = event.key.toLowerCase();
    const canCycle = !labels.some((label) => label[0] === key && label[1] === key);
    if (state.buffer === key && canCycle) {
      state.buffer = "";
      state.previousSearchIndex = state.matchedIndex;
    }
    state.buffer += key;
    clearTimeout(state.timer);
    state.timer = setTimeout(resetClosedTypeahead, CLOSED_TYPEAHEAD_RESET_MS);
    const from = (state.previousSearchIndex ?? -1) + 1;
    for (let offset = 0; offset < labels.length; offset += 1) {
      const index = (from + offset) % labels.length;
      if (labels[index]!.startsWith(state.buffer)) {
        state.matchedIndex = index;
        const match = searchable[index]!;
        if (match.value !== value) onValueChange(match.value);
        return true;
      }
    }
    if (event.key !== " ") resetClosedTypeahead();
    return true;
  };

  const onFieldKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const handled = typeAheadClosed(event);
    const opens = [" ", "ArrowUp", "ArrowDown", "Enter"].includes(event.key);
    if (!handled && opens) {
      event.preventDefault();
      setOpen(true, "keyboard");
    }
  };

  const onListKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    // An option's own keydown may already have chosen, and closed.
    if (!openRef.current) return;
    if (event.key === "Tab") {
      event.preventDefault();
      close();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const list = event.currentTarget;
    const items = optionsIn(list);
    const focused = document.activeElement as HTMLElement | null;
    const onList = focused === list;
    const at = items.indexOf(focused as HTMLElement);
    const moveTo = (index: number) => {
      const item = items[index];
      if (!item) return;
      item.focus();
      setActiveValue(item.dataset.value ?? null);
    };
    if (!event.shiftKey) {
      // The roving walk, without wrapping (`disableListWrap`).
      if (event.key === "ArrowDown") {
        event.preventDefault();
        return moveTo((onList ? -1 : at) + 1);
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        return moveTo((onList ? items.length : at) - 1);
      }
      if (event.key === "Home") {
        event.preventDefault();
        return moveTo(0);
      }
      if (event.key === "End") {
        event.preventDefault();
        return moveTo(items.length - 1);
      }
    }
    if (event.key.length !== 1) return;
    // The keystroke's own time, on `performance.now()`'s clock.
    const { target, handled } = typeaheadStep(
      openTypeahead.current,
      event.key,
      event.timeStamp,
      items,
      at,
      onList ? -1 : at,
    );
    if (target !== null) moveTo(target);
    if (handled) event.preventDefault();
  };

  const selected = options.find((option) => option.value === value);
  const tabStop = activeValue ?? (hasSelected ? value : firstEnabled?.value);

  const layer = (
    <div
      ref={rootRef}
      data-slot="mui-menu-select-root"
      className="fixed inset-0 z-50"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.nativeEvent.isComposing) return;
        // Swallowed, as `useModal.js:118-123` does — and prevented, since
        // stopping it at a React root on the document does not reach a
        // listener on that same node: a snackbar's reads the flag.
        event.stopPropagation();
        event.preventDefault();
        close();
      }}
    >
      {/* The invisible backdrop: a click on it — press and release both
          on it — closes the menu (`useModal.js:125-133`). */}
      <div
        aria-hidden="true"
        data-slot="mui-menu-select-backdrop"
        className="fixed inset-0 -z-1 bg-transparent [-webkit-tap-highlight-color:transparent]"
        onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}
      />
      <div
        ref={attachPaper}
        data-slot="mui-menu-select-paper"
        data-state={open ? "open" : "closed"}
        onAnimationEnd={(event) => {
          if (
            !openRef.current &&
            event.target === event.currentTarget &&
            event.animationName === "mui-grow-out-opacity"
          )
            setPresent(false);
        }}
        className={cn(
          MUI_MENU_SURFACE,
          // `Paper`'s ground and ink (`Paper.js:20-24`). The Radix menus
          // take theirs from the vendored content's `bg-popover`; this
          // paper has no vendored layer under it, and without these the
          // page showed through the menu (#694).
          "text-lennar-ink bg-white",
          "mui-menu-grow absolute min-h-4 min-w-4 overflow-x-hidden overflow-y-auto outline-0",
          "max-h-[calc(100%-96px)] max-w-[calc(100%-32px)]",
        )}
      >
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          onKeyDown={onListKeyDown}
          className={cn(MUI_MENU_LIST, "m-0 list-none outline-0")}
        >
          {options.map((option) => (
            <MenuSelectOption
              key={option.value}
              option={option}
              selected={option.value === value}
              tabStop={option.value === tabStop}
              onFocus={() => setActiveValue(option.value)}
              onChoose={() => choose(option.value)}
              onPress={pressedOnOption}
              releaseChooses={releaseChooses}
            />
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <span
      ref={anchorRef}
      // A row whose whole box is a drag handle never lifts from a select
      // (`isPressOnInteractiveElement`), its in-place menu included.
      data-dnd-interactive=""
      className={cn("relative inline-flex", anchorClassName)}
    >
      <div
        ref={fieldRef}
        id={id}
        role="combobox"
        tabIndex={disabled ? undefined : 0}
        aria-controls={open ? listboxId : undefined}
        aria-disabled={disabled || undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-required={required || undefined}
        onMouseDown={disabled ? undefined : onFieldMouseDown}
        onKeyDown={disabled ? undefined : onFieldKeyDown}
        onBlur={resetClosedTypeahead}
        className={className}
      >
        {selected ? (
          (renderValue?.(selected) ?? selected.content ?? selected.label)
        ) : (
          // An empty field holds its line with a zero-width space (`:731-735`).
          <span className="notranslate" aria-hidden="true">
            {"​"}
          </span>
        )}
      </div>
      {caret?.(open)}
      {present && (disablePortal ? layer : createPortal(layer, portalContainer ?? document.body))}
    </span>
  );
}

function MenuSelectOption(props: MenuSelectOptionProps) {
  return props.option.disabled ? (
    <DisabledMenuSelectOption option={props.option} selected={props.selected} />
  ) : (
    <EnabledMenuSelectOption {...props} />
  );
}

/** MUI's disabled `MenuItem`: 0.38, no pointer, no focus, no ripple. */
function DisabledMenuSelectOption({
  option,
  selected,
}: {
  option: MuiMenuSelectOption;
  selected: boolean;
}) {
  return (
    <li
      role="option"
      aria-selected={selected}
      aria-disabled="true"
      tabIndex={-1}
      data-value={option.value}
      className={cn(MUI_MENU_ITEM, "pointer-events-none opacity-[0.38] select-none")}
    >
      {option.content ?? option.label}
    </li>
  );
}

interface MenuSelectOptionProps {
  option: MuiMenuSelectOption;
  selected: boolean;
  tabStop: boolean;
  onFocus: () => void;
  onChoose: () => void;
  onPress: () => void;
  releaseChooses: (selected: boolean) => boolean;
}

function EnabledMenuSelectOption({
  option,
  selected,
  tabStop,
  onFocus,
  onChoose,
  onPress,
  releaseChooses,
}: MenuSelectOptionProps) {
  const { rippleTargetProps, rippleLayer } = useTouchRipple();
  return (
    <li
      role="option"
      aria-selected={selected}
      tabIndex={tabStop ? 0 : -1}
      data-value={option.value}
      {...composeRippleProps<HTMLLIElement>(rippleTargetProps, { onPointerDown: onPress, onFocus })}
      onMouseDown={onPress}
      // The release of the press that opened the menu, once it was held long
      // enough (`handleItemMouseUp`).
      onMouseUp={(event) => {
        if (releaseChooses(selected)) event.currentTarget.click();
      }}
      onClick={onChoose}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (!event.repeat || event.key === "Enter") event.currentTarget.click();
        }
      }}
      onKeyUp={(event) => {
        // Space chose on the way down; its release must not click again.
        if (event.key === " ") event.preventDefault();
      }}
      className={cn(
        MUI_MENU_ITEM,
        "cursor-pointer select-none",
        "aria-selected:bg-lennar-primary-selected",
        "aria-selected:hover:bg-lennar-primary-selected-hover",
        "aria-selected:focus-visible:bg-lennar-primary-selected-focus",
        // `.Mui-selected:hover` is declared after `.Mui-selected.Mui-focusVisible`
        // at the same specificity, so on a selected row hover wins.
        "aria-selected:focus-visible:hover:bg-lennar-primary-selected-hover",
      )}
    >
      {option.content ?? option.label}
      {rippleLayer}
    </li>
  );
}

/** The rows focus can reach — a disabled one never takes it. */
function optionsIn(list: HTMLElement) {
  return [...list.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])')];
}
